// 1. External Libraries
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MultiValue, SingleValue } from 'react-select';
import { useSelector } from 'react-redux';

// 2. Assets
import GoogleScholar from '../assets/google-scholar.png';
import LinkedIn from '../assets/linkedin-input.png';

// 3. Styles
import '../styles/Invite.css';

// 4. Constants
import { FACULTY } from '../constants/Constants';

// 5. Redux/State Management
import { RootState } from '../redux/store';

// 6. API Services
import { postInviteOthers } from '../api/UserService';

// 7. Types
import {
  InviteFormErrors,
  OptionType,
  UserRole,
  InviteUserResponse,
  InternalServerError,
} from '../types/types';

// 8. Utilities
import {
  validateEmail,
  validateGoogleScholarURL,
  validateLinkedInURL,
  validateName,
} from '../utils/CommonUtils';

// 9. Components
import Label from './common/Label';
import Input from './common/Input';
import CustomSelect from './common/Select';
import Button from './common/Button';
import InputIcon from './common/InputIcon';
import Loading from './common/Loading';
import ActionConfirmationModal from './common/ActionConfirmationModal';
import Text from './common/Text';

const InviteOthers: React.FC = () => {
  const reduxUserTypes = useSelector((state: RootState) => state.userTypes);
  const user = useSelector((state: RootState) => state.user);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    selectedOption: null as SingleValue<OptionType>,
    googleScholarURL: '',
    linkedInURL: '',
  });

  const [isDisplayGoogleScholarURL, setIsDisplayGoogleScholarURL] =
    useState(false);
  const [isDisplayLinkedinURL, setIsDisplayLinkedinURL] = useState(false);
  const [errors, setErrors] = useState<InviteFormErrors>({});
  const [options, setOptions] = useState<OptionType[]>([]);
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const apiOptions: OptionType[] = [];
    reduxUserTypes.userTypes.map((type: UserRole) => {
      apiOptions.push({
        label: type.type,
        value: type.id as number,
      });
    });
    setOptions(apiOptions);
  }, [reduxUserTypes.userTypes]);

  const handleInviteOthersSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    setLoading(true);
    const newErrors: InviteFormErrors = {};
    const {
      firstName,
      lastName,
      email,
      selectedOption,
      googleScholarURL,
      linkedInURL,
    } = formData;

    if (!firstName) newErrors.firstName = 'Please enter a first name.';
    else if (firstName.length < 2 || !validateName(firstName))
      newErrors.firstName =
        'Please enter a first name that is at least 2 characters.';
    if (!lastName) newErrors.lastName = 'Please enter a last name.';
    else if (lastName.length < 2 || !validateName(lastName))
      newErrors.lastName =
        'Please enter a last name that is at least 2 characters.';
    if (!email) newErrors.email = 'Please enter a email address.';
    else if (!validateEmail(email))
      newErrors.email = 'Please enter a valid email address.';
    if (!selectedOption)
      newErrors.userType =
        'Please choose current involvement with the University.';

    // we made this as option
    // if (isDisplayGoogleScholarURL && !googleScholarURL)
    //   newErrors.googleScholarURL = 'Google Scholar URL is required';
    // else
    if (googleScholarURL && !validateGoogleScholarURL(googleScholarURL))
      newErrors.googleScholarURL = 'Please enter a valid Google Scholar URL.';

    if (isDisplayLinkedinURL && !linkedInURL)
      newErrors.linkedInURL = 'Please enter a LinkedIn URL.';
    else if (linkedInURL && !validateLinkedInURL(linkedInURL))
      newErrors.linkedInURL = 'Please enter a valid LinkedIn URL.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    } else {
      setErrors({});
      const invitedUserId =
        user.userDetails?.userId !== undefined ? user.userDetails.userId : 0;
      const result: InviteUserResponse | InternalServerError =
        await postInviteOthers({
          tenant_id: 1,
          first_name: firstName,
          last_name: lastName,
          email: email,
          user_type_id: selectedOption?.value as number,
          invited_user_id: invitedUserId,
          google_scholar_url: googleScholarURL,
          linkedin_url: linkedInURL,
        });
      if ('code' in result) {
        // Handle the InternalServerError
        console.error('Error post an invite others:', result);
        setLoading(false);
        const details = result?.details ?? [];
        switch (result.code) {
          case 'BAD_REQUEST':
            setMessage(
              details.length > 0
                ? details[0].error
                : 'An unknown error occurred.',
            );
            break;
          case 'CONFLICT':
            setMessage(result.message);
            break;
          case 'INVALID_REQUEST':
          case 'UNKNOWN_ERROR':
            setMessage(result.message || 'An unknown error occurred.');
            break;
          case 'DUPLICATE_RECORD_ERROR':
            setMessage(result.message);
            break;
          default:
            setMessage('An unexpected error occurred.');
        }
      } else {
        if (result.status === 201) {
          setIsModalOpen(true);
          setLoading(false);
        } else {
          setLoading(false);
          console.log('Unexpected response:', result);
        }
      }
    }
  };

  const handleChange = (
    option: SingleValue<OptionType> | MultiValue<OptionType> | null,
  ) => {
    setErrors({});
    if (option === null) {
      setIsDisplayGoogleScholarURL(false);
      setIsDisplayLinkedinURL(false);
      setFormData((prev) => ({ ...prev, selectedOption: null }));
      return;
    }

    if ('value' in option) {
      // SingleValue<UserType>
      if (option.value == FACULTY) {
        setIsDisplayGoogleScholarURL(true);
        setIsDisplayLinkedinURL(true);
      } else {
        setIsDisplayLinkedinURL(true);
        setIsDisplayGoogleScholarURL(false);
      }
      setFormData((prev) => ({ ...prev, selectedOption: option }));
    } else {
      // MultiValue<UserType>
      // Handle multi-select case if necessary
      // For single select use case, you might not need this part.
      // Optionally add logic if needed for multi-select.
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage(null);
    setErrors({});
  };

  const handleInviteOthers = () => {
    setIsModalOpen(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      selectedOption: null as SingleValue<OptionType> | null,
      googleScholarURL: '',
      linkedInURL: '',
    });
    setIsDisplayGoogleScholarURL(false);
    setIsDisplayLinkedinURL(false);
    navigate('/invite-others');
    setMessage(null);
  };

  const handleGoToDashboard = () => {
    setIsModalOpen(false);
    navigate('/dashboard');
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  return (
    <div className="common-container padding-container custom-background">
      <div className="auth-container">
        <div className="grid gap-8">
          <Text className="display-extra-small">Invite Others</Text>
          <Text className="description">
            Invite others to connect with mentors, collaborators, and
            opportunities that drive their entrepreneurial success.
          </Text>
        </div>
        <form
          onSubmit={handleInviteOthersSubmit}
          className="padding-0 grid gap-24"
          noValidate
        >
          <div className="grid gap-20">
            <div className="myclass d-flex gap-24">
              <div className="auth-input-group">
                <Label htmlFor="firstName" className="common-label">
                  First name<sup className="fs-12">*</sup>
                </Label>
                <Input
                  type="text"
                  className="auth-input-first-last-name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  aria-describedby="firstNameError"
                />
                {errors.firstName && (
                  <div id="firstNameError" className="error-message">
                    {errors.firstName}
                  </div>
                )}
              </div>
              <div className="auth-input-group">
                <Label htmlFor="lastName" className="common-label">
                  Last name<sup className="fs-12">*</sup>
                </Label>
                <Input
                  type="text"
                  className="auth-input-first-last-name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  aria-describedby="lastNameError"
                />
                {errors.lastName && (
                  <div id="lastNameError" className="error-message">
                    {errors.lastName}
                  </div>
                )}
              </div>
            </div>
            <div className="auth-input-group">
              <Label htmlFor="email" className="common-label">
                Email<sup className="fs-12">*</sup>
              </Label>
              <Input
                type="text"
                className="auth-input"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email"
                aria-describedby="emailError"
              />
              {errors.email && (
                <div id="emailError" className="error-message">
                  {errors.email}
                </div>
              )}
            </div>
            <div className="auth-input-group">
              <Label htmlFor="email" className="common-label">
                University Role<sup className="fs-12">*</sup>
              </Label>
              <CustomSelect
                options={options}
                onChange={handleChange}
                value={formData.selectedOption}
                placeholder="Select Role"
                isMulti={false}
                isClearable={true}
                aria-describedby="userTypeError"
              />
              {errors.userType && (
                <div id="userTypeError" className="error-message">
                  {errors.userType}
                </div>
              )}
            </div>
            {isDisplayGoogleScholarURL && (
              <div className="auth-input-group">
                <Label htmlFor="googleScholarURL" className="common-label">
                  Google Scholar URL
                </Label>
                <InputIcon
                  name="googleScholarURL"
                  value={formData.googleScholarURL}
                  onChange={handleInputChange}
                  placeholder="Enter Google Scholar URL"
                  aria-describedby="googleScholarURLError"
                  icon={GoogleScholar}
                />
                {errors.googleScholarURL && (
                  <div id="googleScholarURLError" className="error-message">
                    {errors.googleScholarURL}
                  </div>
                )}
              </div>
            )}
            {isDisplayLinkedinURL && (
              <div className="auth-input-group">
                <Label htmlFor="linkedInURL" className="common-label">
                  LinkedIn URL<sup className="fs-12">*</sup>
                </Label>
                <InputIcon
                  name="linkedInURL"
                  value={formData.linkedInURL}
                  onChange={handleInputChange}
                  placeholder="Enter LinkedIn URL"
                  icon={LinkedIn}
                />
                {errors.linkedInURL && (
                  <div id="linkedInURLError" className="error-message">
                    {errors.linkedInURL}
                  </div>
                )}
              </div>
            )}
            {message && (
              <div className="error-message">
                <p>
                  {message.includes('already exists.') ? (
                    <>
                      {
                        'An account or invitation with this email already exists. Enter a different email.'
                      }
                    </>
                  ) : (
                    <>{message}</>
                  )}
                </p>
              </div>
            )}
          </div>
          <Button
            type="submit"
            className="tertiary-button"
            disabled={loading != null && loading}
          >
            {loading ? (
              <>
                <Loading size={25} /> &nbsp; Send Invitation
              </>
            ) : (
              'Send Invitation'
            )}
          </Button>
        </form>
        <ActionConfirmationModal
          title="Success"
          message="We've sent your invitation."
          isFirstButtonRequired={true}
          onFirstButtonAction={handleInviteOthers}
          onFirstButtonName="Invite Others"
          isSecondButtonRequired={true}
          onSecondButtonAction={handleGoToDashboard}
          onSecondButtonName="Go to Dashboard"
          isOpen={isModalOpen}
        />
      </div>
    </div>
  );
};

export default InviteOthers;
